import { useState } from 'react';

import { useFormContext } from 'react-hook-form';

import { TCompanyMatch } from './companyData';
import { CompanyLoading } from './CompanyLoading/CompanyLoading';
import { CompanyManualEntry } from './CompanyManualEntry/CompanyManualEntry';
import { CompanySearch } from './CompanySearch/CompanySearch';
import { CompanySelected } from './CompanySelected/CompanySelected';
import { fetchGetLegalForm } from '../../../../api/lendingApi/apiQueries';
import logger from '../../../../utils/logger';
import { TMobileSignupForm } from '../../form/form';
import { useStageFieldTracking } from '../../form/MobileSignupFieldTracking';
import { useMobileSignupNavContext } from '../../hooks/useMobileSignupNavContext';

type TMode = 'search' | 'loading' | 'selected' | 'manual';

// Fields that make the company step complete. Shared with registry.ts so the
// advance gate and the step's isAnswered can't drift.
export const REQUIRED_COMPANY_FIELDS: (keyof TMobileSignupForm)[] = [
  'companyName',
  'street',
  'buildingNumber',
  'postcode',
  'town',
  'legalForm',
];

// DIT-680/681/682. Company step orchestrator: register search → loading →
// confirmation card, or manual entry (chosen from search or after a failed
// legal-form lookup). Each state is its own StepLayout-owning screen. Query
// lives here so deselecting a company returns to the prior search; the manual
// screen reads/writes the shared form store directly.
export const CompanyScreen = () => {
  const { getValues, setValue } = useFormContext<TMobileSignupForm>();
  const { goNext } = useMobileSignupNavContext();
  const { reportCompletion } = useStageFieldTracking();

  // Back-nav remounts this screen; the shared store persists company fields
  // (shouldUnregister: false), so derive the mount-time state from it rather
  // than always dropping to search. The manual flag wins first: a crefoId means
  // a register match, but a manually-completed one keeps its crefoId, so only
  // the flag distinguishes the confirmation card from manual entry.
  const initial = getValues();
  const storedCompany: TCompanyMatch | undefined =
    initial.crefoId && !initial.companyEnteredManually
      ? {
          crefo_id: initial.crefoId,
          name: initial.companyName,
          street: initial.street,
          house_number: initial.buildingNumber,
          postcode: initial.postcode,
          city: initial.town,
        }
      : undefined;
  const initialMode: TMode = storedCompany
    ? 'selected'
    : initial.companyEnteredManually || initial.companyName
      ? 'manual'
      : 'search';

  const [mode, setMode] = useState<TMode>(initialMode);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<TCompanyMatch | undefined>(
    storedCompany,
  );
  const [legalForm, setLegalForm] = useState<string | undefined>(
    initial.legalForm,
  );

  const clearCompanyFields = () => {
    // Mirror the deferred completion that handleSelect reported, so returning to
    // search doesn't leave companyName counted in fields_completed.
    reportCompletion('companyName', false);
    setValue('companyName', '');
    setValue('crefoId', '');
    setValue('companyEnteredManually', false);
    setValue('street', '');
    setValue('buildingNumber', '');
    setValue('postcode', '');
    setValue('town', '');
    setValue('legalForm', undefined);
  };

  // "Not listed" — carry the typed query over as the company name, then let
  // the user complete the rest manually.
  const enterManually = () => {
    setValue('companyName', query);
    setValue('companyEnteredManually', true);
    setMode('manual');
  };

  const backToSearch = () => {
    clearCompanyFields();
    setQuery('');
    setMode('search');
  };

  // The register can return a match with gaps (e.g. no house number). Advance
  // only when every required field is present; otherwise drop into the
  // (already-seeded) manual screen so the user completes the rest.
  const confirmSelected = () => {
    const values = getValues();
    const isComplete = REQUIRED_COMPANY_FIELDS.every(
      (field) => !!values[field],
    );
    if (isComplete) {
      goNext();
    } else {
      setValue('companyEnteredManually', true);
      setMode('manual');
    }
  };

  const handleSelect = async (company: TCompanyMatch) => {
    setSelected(company);
    setLegalForm(undefined);
    setValue('companyEnteredManually', false);
    setMode('loading');

    // Persist the matched company into the form store. The legal form follows
    // from the separate lookup below; on failure the fields are already there
    // for the manual fall-through to complete.
    setValue('companyName', company.name);
    reportCompletion('companyName', true);
    setValue('crefoId', company.crefo_id);
    setValue('buildingNumber', company.house_number);
    setValue('street', company.street);
    setValue('postcode', company.postcode);
    setValue('town', company.city);

    try {
      const response = await fetchGetLegalForm(company.crefo_id);

      if (!response?.data?.legal_form)
        throw new Error('No legal form returned');

      setValue('legalForm', response.data.legal_form);
      setLegalForm(response.data.legal_form);
      setMode('selected');
    } catch {
      logger.error('MobileSignup: legal form fetch failed', {
        tags: { team: 'de-introducer' },
        crefoId: company.crefo_id,
      });
      // Fall through to manual entry to complete the (already-seeded) details.
      // Flag it so back-nav re-opens manual entry, not the confirmation card,
      // despite the crefoId the register match left behind.
      setValue('companyEnteredManually', true);
      setMode('manual');
    }
  };

  if (mode === 'loading') return <CompanyLoading />;

  if (mode === 'selected' && selected)
    return (
      <CompanySelected
        company={selected}
        legalForm={legalForm}
        onConfirm={confirmSelected}
        onDeselect={() => {
          clearCompanyFields();
          setSelected(undefined);
          setMode('search');
        }}
      />
    );

  if (mode === 'manual')
    return <CompanyManualEntry onBackToSearch={backToSearch} />;

  return (
    <CompanySearch
      query={query}
      onQueryChange={setQuery}
      onSelect={handleSelect}
      onEnterManually={enterManually}
    />
  );
};
